import { memo, useCallback, useMemo } from 'react';
import { Obstacle } from '@thive/fddm-types';

import { GapHorizontalIcon } from '@/assets/icons';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { MIN_WALL_THICKNESS } from '@/modules/floorplanDesign/constants/obstacle';
import { NumUtils } from '@/modules/common/utils';

interface Props {
  selectedWall: Obstacle;
}

export const ThicknessComponent = ({ selectedWall }: Props) => {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const value = useMemo(() => {
    if (!selectedWall.segments || selectedWall.segments.length === 0) return;
    return NumUtils.metersToMmRounded(
      selectedWall.segments[0].leftSideWidth + selectedWall.segments[0].rightSideWidth,
    );
  }, [selectedWall.segments]);

  const onChange = useCallback(
    (newValueMm: number) => {
      if (selectedWall.appearance !== 'WALL') return;

      if (Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);

      fddmSvc.setPreference('highwayArea.segment.width', newValue);
      fddmSvc.updateObstacle({
        ...selectedWall,
        segments: minItemsArrayUtils.mapAtLeast1(selectedWall.segments, (segment) => ({
          ...segment,
          leftSideWidth: newValue / 2,
          rightSideWidth: newValue / 2,
        })),
      });
    },
    [selectedWall, fddmSvc],
  );

  if (
    selectedWall.segments === undefined ||
    selectedWall.segments.length <= 1 ||
    value === undefined
  )
    return null;

  return (
    <PropertiesLabel i18nkey='interface:properties.highway.width'>
      <NumberInput
        value={value}
        unit='mm'
        prefix={<GapHorizontalIcon />}
        sx={{ width: '100%' }}
        minValue={NumUtils.metersToMmRounded(MIN_WALL_THICKNESS)}
        onChange={onChange}
      />
    </PropertiesLabel>
  );
};

export const Thickness = memo(ThicknessComponent);
